import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Avatar,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import S3FileUpload from 'react-s3';
import { updateProfileclient } from "./action/auth";
import { useAuthToken } from "../TokenContext";
import PropTypes from 'prop-types';
import LanguageSelector from '../LanguageSelector';
import defaultimg from './assets/defaultimge.png';
import { useIntl } from 'react-intl';
import { useCredentials } from '../useCredentials';
import { styled } from '@mui/material/styles';

/* ── Styled Components (Artemy dark theme) ───────────────────────── */

const ProfileContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: 760,
  margin: '0 auto',
  padding: '4px',
}));

const ProfileCard = styled(Card)(() => ({
  background: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  borderRadius: 14,
  overflow: 'visible',
  transition: 'border-color 0.2s',
  '&:hover': { borderColor: 'var(--artemy-border-bright)' },
  boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
}));

const ProfileHeader = styled(Box)(() => ({
  height: 140,
  background: 'linear-gradient(135deg, #0f1a3a 0%, #1a2d6b 50%, #0d3060 100%)',
  position: 'relative',
  borderRadius: '14px 14px 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledLanguageSelector = styled(Box)(() => ({
  position: 'absolute',
  top: 12,
  right: 14,
  zIndex: 2,
  backgroundColor: 'rgba(8,9,13,0.65)',
  borderRadius: 8,
  padding: 4,
  border: '1px solid var(--artemy-border)',
  backdropFilter: 'blur(8px)',
}));

const ProfileAvatarWrapper = styled(Box)(() => ({
  position: 'absolute',
  left: '50%',
  bottom: -54,
  transform: 'translateX(-50%)',
  zIndex: 2,
}));

const ProfileAvatar = styled(Avatar)(() => ({
  width: 108,
  height: 108,
  border: '3px solid var(--artemy-bg-card)',
  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  transition: 'transform 0.3s ease',
  '&:hover': { transform: 'scale(1.05)' },
}));

const UploadOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  borderRadius: '50%',
  cursor: 'pointer',
  '&:hover': { opacity: 1 },
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)', boxShadow: '0 0 0 2px rgba(61,127,255,0.12)' },
    '& input, & textarea': { color: 'var(--artemy-text-primary)', fontSize: 13 },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-secondary)', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
  marginBottom: '20px',
}));

const StyledFormControl = styled(FormControl)(() => ({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' },
    '& .MuiSelect-icon': { color: 'var(--artemy-text-secondary)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-secondary)', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 8,
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontFamily: 'var(--artemy-font-body)',
}));

const InfoSection = styled(Box)(() => ({
  padding: '20px',
  backgroundColor: 'var(--artemy-bg-elevated)',
  borderRadius: 10,
  border: '1px solid var(--artemy-border)',
  marginBottom: '20px',
}));

/* ── Component ──────────────────────────────────────────────────── */

const Clientprofile = ({ currentLocale, changeLocale, collapsed }) => {
  const { credentials } = useCredentials();
  const config = useMemo(() => ({
    bucketName: credentials.bucketName,
    region: credentials.region,
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);

  const history = useHistory();
  const dispatch = useDispatch();
  const token = useAuthToken();
  const { user } = useSelector(state => state.auth);
  const intl = useIntl();
  const theme = useTheme();

  const [state, setState] = useState({
    fname: user.data[0].emp_firstname || '',
    mname: user.data[0].emp_middlename || '',
    lname: user.data[0].emp_lastname || '',
    email: user.data[0].email || '',
    phone_number: user.data[0].phone_number || '',
    country: user.data[0].emp_country || '',
    about: user.data[0].about || '',
    profilePicture: user.data[0].profilePicture || ''
  });

  const [edit, setEdit] = useState(user.data[0].profile === 0);
  const [image, setImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [authImage, setAuthImage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { generatePreSignedUrl(); }, []);

  const generatePreSignedUrl = async () => {
    if (!user.data[0].profilePicture) return;
    try {
      const response = await fetch(process.env.REACT_APP_TALENT_CLOUDFRONTAPI, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: process.env.REACT_APP_CLOUDFRONT + user.data[0].profilePicture })
      });
      const data = await response.json();
      const signedUrl = JSON.parse(data.body).signedUrl;
      setAuthImage(signedUrl);
    } catch (error) {
      console.error("Error generating signed URL:", error);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const preview = URL.createObjectURL(file);
      setImage(preview);
      const uploadResult = await S3FileUpload.uploadFile(file, config);
      setState(prev => ({ ...prev, profilePicture: uploadResult.key }));
      setAuthImage(`https://${config.bucketName}.s3.${config.region}.amazonaws.com/${uploadResult.key}`);
      setSnackbar({ open: true, message: 'Image uploaded successfully', severity: 'success' });
    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({ open: true, message: 'Failed to upload image', severity: 'error' });
      setImage('');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const data = {
        emp_id: user.data[0].emp_id,
        emp_firstname: state.fname,
        emp_middlename: state.mname,
        emp_lastname: state.lname,
        phone_number: state.phone_number,
        emp_country: state.country,
        about: state.about,
        profilePicture: state.profilePicture
      };
      await dispatch(updateProfileclient(data, {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }));
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
      setEdit(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update profile', severity: 'error' });
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setState(prev => ({ ...prev, [id]: value }));
  };

  const countries = [
    { value: 'usa', label: 'USA' }, { value: 'canada', label: 'Canada' },
    { value: 'india', label: 'India' }, { value: 'australia', label: 'Australia' },
    { value: 'united_kingdom', label: 'United Kingdom' }, { value: 'germany', label: 'Germany' },
    { value: 'france', label: 'France' }, { value: 'china', label: 'China' },
    { value: 'japan', label: 'Japan' }, { value: 'brazil', label: 'Brazil' },
    { value: 'south_africa', label: 'South Africa' }, { value: 'mexico', label: 'Mexico' },
    { value: 'italy', label: 'Italy' }, { value: 'spain', label: 'Spain' },
    { value: 'russia', label: 'Russia' }, { value: 'south_korea', label: 'South Korea' },
    { value: 'new_zealand', label: 'New Zealand' }, { value: 'singapore', label: 'Singapore' },
    { value: 'united_arab_emirates', label: 'United Arab Emirates' },
    { value: 'saudi_arabia', label: 'Saudi Arabia' }, { value: 'argentina', label: 'Argentina' },
    { value: 'netherlands', label: 'Netherlands' }, { value: 'sweden', label: 'Sweden' },
    { value: 'switzerland', label: 'Switzerland' }, { value: 'norway', label: 'Norway' },
    { value: 'denmark', label: 'Denmark' }, { value: 'finland', label: 'Finland' },
    { value: 'portugal', label: 'Portugal' }, { value: 'poland', label: 'Poland' },
    { value: 'turkey', label: 'Turkey' }, { value: 'indonesia', label: 'Indonesia' },
    { value: 'malaysia', label: 'Malaysia' }, { value: 'thailand', label: 'Thailand' },
    { value: 'vietnam', label: 'Vietnam' }, { value: 'philippines', label: 'Philippines' },
    { value: 'egypt', label: 'Egypt' }, { value: 'nigeria', label: 'Nigeria' },
    { value: 'kenya', label: 'Kenya' }, { value: 'pakistan', label: 'Pakistan' },
    { value: 'bangladesh', label: 'Bangladesh' }, { value: 'sri_lanka', label: 'Sri Lanka' },
    { value: 'nepal', label: 'Nepal' }, { value: 'qatar', label: 'Qatar' },
    { value: 'oman', label: 'Oman' }, { value: 'kuwait', label: 'Kuwait' },
    { value: 'israel', label: 'Israel' }, { value: 'colombia', label: 'Colombia' },
    { value: 'chile', label: 'Chile' }, { value: 'peru', label: 'Peru' },
  ];

  return (
    <ProfileContainer>
      {/* Header */}
      <Box sx={{ mb: 3, borderBottom: '1px solid var(--artemy-border)', pb: 2 }}>
        <Typography sx={{ fontFamily: 'var(--artemy-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--artemy-text-primary)', letterSpacing: '-0.4px' }}>
          {intl.formatMessage({ id: 'manageProfile' })}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-secondary)', mt: 0.5 }}>
          Manage your account details and preferences
        </Typography>
      </Box>

      <ProfileCard>
        <ProfileHeader>
          <StyledLanguageSelector>
            <LanguageSelector currentLocale={currentLocale} changeLocale={changeLocale} />
          </StyledLanguageSelector>

          <ProfileAvatarWrapper>
            <Box sx={{ position: 'relative' }}>
              <ProfileAvatar src={image || authImage || defaultimg} alt={state.fname} />
              {edit && (
                <UploadOverlay>
                  <label htmlFor="profile-image">
                    <input accept="image/*" type="file" hidden id="profile-image" onChange={handleImageUpload} />
                    <IconButton component="span" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                      <PhotoCameraIcon />
                    </IconButton>
                  </label>
                </UploadOverlay>
              )}
            </Box>
          </ProfileAvatarWrapper>
        </ProfileHeader>

        {!edit && (
          <IconButton
            sx={{
              position: 'absolute',
              right: 16,
              top: 150,
              backgroundColor: 'var(--artemy-bg-elevated)',
              border: '1px solid var(--artemy-border)',
              color: 'var(--artemy-text-secondary)',
              '&:hover': { backgroundColor: 'var(--artemy-bg-card)', color: 'var(--artemy-accent)' }
            }}
            onClick={() => setEdit(true)}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        <CardContent sx={{ pt: 10, px: 3, pb: 3 }}>
          {edit ? (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <StyledTextField fullWidth label={intl.formatMessage({ id: 'firstName' })} id="fname" value={state.fname} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StyledTextField fullWidth label={intl.formatMessage({ id: 'middleName' })} id="mname" value={state.mname} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StyledTextField fullWidth label={intl.formatMessage({ id: 'lastName' })} id="lname" value={state.lname} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField fullWidth label={intl.formatMessage({ id: 'phoneNumber' })} id="phone_number" value={state.phone_number} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledFormControl fullWidth>
                    <InputLabel id="country-label">{intl.formatMessage({ id: 'country' })}</InputLabel>
                    <Select
                      labelId="country-label"
                      id="country"
                      value={state.country}
                      onChange={(e) => setState(prev => ({ ...prev, country: e.target.value }))}
                      label={intl.formatMessage({ id: 'country' })}
                      required
                      MenuProps={{ PaperProps: { style: { maxHeight: 300, background: 'var(--artemy-bg-elevated)' } } }}
                    >
                      <MenuItem value=""><em>Select a country</em></MenuItem>
                      {countries.map(c => <MenuItem key={c.value} value={c.value} sx={{ color: 'var(--artemy-text-primary)', fontSize: 13 }}>{c.label}</MenuItem>)}
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth multiline rows={4}
                    label={intl.formatMessage({ id: 'about' })}
                    id="about"
                    value={state.about}
                    inputProps={{ maxLength: 1000 }}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <ActionButton variant="outlined" onClick={() => setEdit(false)} startIcon={<CloseIcon />}
                  sx={{ color: 'var(--artemy-text-secondary)', borderColor: 'var(--artemy-border)', '&:hover': { borderColor: 'var(--artemy-border-bright)' } }}>
                  {intl.formatMessage({ id: 'cancel' })}
                </ActionButton>
                <ActionButton type="submit" variant="contained" startIcon={<SaveIcon />} disabled={uploading}
                  sx={{ background: 'var(--artemy-accent)', '&:hover': { background: '#3570e5', boxShadow: '0 4px 14px rgba(61,127,255,0.35)' } }}>
                  {uploading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : intl.formatMessage({ id: user.data[0].profile === 0 ? 'create' : 'save' })}
                </ActionButton>
              </Box>
            </form>
          ) : (
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, textAlign: 'center', color: 'var(--artemy-text-primary)', fontFamily: 'var(--artemy-font-display)', mb: 0.5 }}>
                {`${state.fname} ${state.lname}`}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-dim)', textAlign: 'center', fontFamily: 'var(--artemy-font-mono)', mb: 3 }}>
                {state.email}
              </Typography>

              <InfoSection>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--artemy-accent)', fontWeight: 600, mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: 11, color: 'var(--artemy-text-dim)', mb: 0.4, fontFamily: 'var(--artemy-font-mono)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Phone
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--artemy-text-primary)' }}>{state.phone_number || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: 11, color: 'var(--artemy-text-dim)', mb: 0.4, fontFamily: 'var(--artemy-font-mono)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Country
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--artemy-text-primary)' }}>{state.country || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 11, color: 'var(--artemy-text-dim)', mb: 0.4, fontFamily: 'var(--artemy-font-mono)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      About
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-secondary)', lineHeight: 1.7 }}>{state.about || '—'}</Typography>
                  </Grid>
                </Grid>
              </InfoSection>
            </Box>
          )}
        </CardContent>
      </ProfileCard>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 8 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {uploading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(8,9,13,0.85)', zIndex: 9999 }}>
          <CircularProgress sx={{ color: 'var(--artemy-accent)' }} />
        </Box>
      )}
    </ProfileContainer>
  );
};

Clientprofile.propTypes = {
  changeLocale: PropTypes.func.isRequired,
  currentLocale: PropTypes.string.isRequired,
};

export default Clientprofile;